/**
 * LmsCourses — Admin course library.
 * Feb 2026 R9 · Ships with LMS module.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Plus, Sparkles, Users, Clock, Award, Trash2 } from "lucide-react";

export default function LmsCourses() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [genAudience, setGenAudience] = useState("");
  const [genModulesHint, setGenModulesHint] = useState(4);
  const [genLessonsHint, setGenLessonsHint] = useState(3);
  const [genCert, setGenCert] = useState(true);
  const [genBusy, setGenBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lms/courses`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setBusy(false); }
  }, [activeWorkspaceId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!genPrompt.trim() || genPrompt.trim().length < 10) {
      toast.error("Prompt must be at least 10 characters");
      return;
    }
    setGenBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/lms/courses/generate`,
        {
          prompt: genPrompt,
          audience: genAudience || null,
          modules_hint: Number(genModulesHint) || null,
          lessons_per_module_hint: Number(genLessonsHint) || null,
          include_quiz: true,
          cert_enabled: genCert,
        });
      toast.success(`Course "${data.title}" generated`);
      setGenOpen(false);
      setGenPrompt("");
      navigate(`/app/lms/courses/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "AI generation failed")); }
    finally { setGenBusy(false); }
  };

  const createBlank = async () => {
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/lms/courses`,
        { title: "New course", description: "", pass_percent: 70, cert_enabled: true });
      navigate(`/app/lms/courses/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Learning · Courses"
        description="AI-generated courses with modules, lessons, quizzes and certificates"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none h-9" onClick={createBlank} data-testid="lms-new-blank">
              <Plus className="w-4 h-4 mr-1" /> Blank course
            </Button>
            <Button className="rounded-none h-9" onClick={() => setGenOpen(true)} data-testid="lms-generate-open">
              <Sparkles className="w-4 h-4 mr-1" /> AI generate
            </Button>
          </div>
        }
      />

      {busy && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!busy && rows.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center">
          <BookOpen className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No courses yet. Click <b>AI generate</b> to build one from a prompt.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((c) => (
          <div key={c.id} className="border border-border p-4 hover:border-primary cursor-pointer" onClick={() => navigate(`/app/lms/courses/${c.id}`)} data-testid={`lms-course-${c.id}`}>
            <div className="flex items-start gap-2">
              <BookOpen className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{c.title}</div>
                <div className="text-[10px] uppercase text-muted-foreground mt-0.5">{c.category || "Uncategorised"} · {c.status}</div>
              </div>
            </div>
            {c.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-2">{c.description}</p>}
            <div className="flex items-center gap-3 mt-3 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.enrollment_count || 0}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {c.duration_min || 0}m</span>
              <span className="inline-flex items-center gap-1">{c.module_count || 0} mod · {c.lesson_count || 0} lessons</span>
              {c.cert_enabled && <Award className="w-3 h-3 text-amber-500" title="Certificate enabled" />}
            </div>
          </div>
        ))}
      </div>

      {/* AI generation dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="lms-generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate a course from a prompt</DialogTitle>
            <DialogDescription>Claude Sonnet 4.5 will draft modules, lessons with 500-1500 word bodies, and a summative quiz. Takes 20-60 seconds.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs mb-1 block">Course prompt *</Label>
              <Textarea value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} rows={4}
                             placeholder="e.g. Client-services fundamentals for junior account managers: escalation etiquette, retention playbooks, feedback loops."
                             className="rounded-none" data-testid="lms-gen-prompt" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Target audience</Label>
              <Input value={genAudience} onChange={(e) => setGenAudience(e.target.value)} className="h-9 rounded-none" placeholder="e.g. Junior AMs, 0-2 yrs" data-testid="lms-gen-audience" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs mb-1 block">Modules (approx)</Label>
                <Input type="number" min={1} max={12} value={genModulesHint} onChange={(e) => setGenModulesHint(e.target.value)} className="h-9 rounded-none" data-testid="lms-gen-modules" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Lessons / module</Label>
                <Input type="number" min={1} max={8} value={genLessonsHint} onChange={(e) => setGenLessonsHint(e.target.value)} className="h-9 rounded-none" data-testid="lms-gen-lessons" />
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-xs">
              <Switch checked={genCert} onCheckedChange={setGenCert} data-testid="lms-gen-cert" /> Enable completion certificate
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={generate} disabled={genBusy} data-testid="lms-gen-run">
              {genBusy ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
